/**
 * Year Filter Component
 * 
 * Horizontal pill selector for year filtering.
 * Shows available years (2025, 2026).
 * Updates the global year store on selection.
 * 
 * Style matches existing Quick Filters (Active, Draft, etc.)
 */
import { Button } from '@/components/ui/button';
import { Calendar } from 'lucide-react';
import { useYear, useSetYear } from '@/state/yearStore';
import { cn } from '@/lib/utils';

interface YearFilterProps {
  /** Optional className for container */
  className?: string;
  /** Whether to show the calendar icon. Default: true */
  showIcon?: boolean;
  /** Label text. Default: "Year" */
  label?: string;
  /** Called after the year changes via user interaction */
  onYearChange?: (year: number) => void;
}

/** Available years for filtering */
const AVAILABLE_YEARS = [2025, 2026];

export function YearFilter({
  className,
  showIcon = true,
  label = 'Year',
  onYearChange,
}: YearFilterProps) {
  const year = useYear();
  const setYear = useSetYear();

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {showIcon && (
        <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>{label}:</span>
        </div>
      )}
      
      <div className="flex flex-wrap items-center gap-1.5">
        {AVAILABLE_YEARS.map((y) => (
          <Button
            key={y}
            variant={year === y ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setYear(y);
              onYearChange?.(y);
            }}
            className={cn(
              'h-8 px-3 text-sm font-medium transition-all',
              year === y 
                ? 'bg-primary text-primary-foreground shadow-sm' 
                : 'hover:bg-muted'
            )}
          >
            {y}
          </Button>
        ))}
      </div>
    </div>
  );
}

export default YearFilter;

